import React from 'react';
import { useBranding } from './BrandingContext';

/*
  The customer's own logo, drawn wherever a screen wants to say whose site this
  is. The counterpart to OnHandLogo: that one is OnHand and never changes,
  CompanyLogo is whatever the company uploaded under Settings.

  Uploaded artwork arrives at whatever aspect ratio the company has - a tall
  crest, a long horizontal lockup - so height is fixed and width is allowed to
  run up to maxWidth. Nothing is ever cropped or stretched.

  A company that has not uploaded anything falls back to its name, set in the
  same weight as the height it was given. Rendering nothing would leave a hole
  in the header and read as a broken image.

  fallbackToName={false} turns that off, for the one placement where the hole
  argument does not hold: the top bar stacks this above the user block, which
  already names the site. companyName IS current.name (see BrandingContext), so
  the fallback there would print the site name twice, one line apart.
*/
export default function CompanyLogo({ height = 30, maxWidth = 150, align = 'left', fallbackToName = true }) {
  const { companyLogoUrl, companyName } = useBranding();

  if (companyLogoUrl) {
    return (
      <img
        src={companyLogoUrl}
        alt={companyName ? companyName + ' logo' : 'Company logo'}
        style={{
          height,
          maxWidth,
          objectFit: 'contain',
          objectPosition: align + ' center',
          display: 'block',
        }}
      />
    );
  }

  if (!fallbackToName || !companyName) return null;

  return (
    <span
      title={companyName}
      style={{
        display: 'block',
        maxWidth,
        fontSize: Math.round(height * 0.46),
        fontWeight: 700,
        letterSpacing: '-0.01em',
        lineHeight: 1.15,
        color: 'var(--text-secondary)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: align,
      }}
    >
      {companyName}
    </span>
  );
}

/*
  The thin rule that separates the OnHand lockup from the company's logo. Kept
  here rather than inline so every co-branded placement draws the same one.
*/
export function CoBrandDivider({ height = 26 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 1,
        height,
        background: 'var(--border)',
        flexShrink: 0,
        display: 'block',
      }}
    />
  );
}
